import { createColumnHelper } from '@tanstack/react-table';
import { array } from 'fp-ts';
import { pipe } from 'fp-ts/function';
import { LocalizedEnum, lt } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { ColumnFilterType, IconName, Link, Position, Stack, Tag } from '@optimus-web/core';
import { values } from '@mews/fp';
import { RateType, RateOverview } from '@mews-commander/data-access';
import { Dash, EnterpriseLink, LocalizedTextTag, TooltippedIconButton } from '@mews-commander/ui';
import { useRateSidePanelContext } from '../components/contexts/RateSidePanelContext';
import { RowActionCell } from './RowActionCell';

const columnHelper = createColumnHelper<RateOverview>();

const enabledstatusOptions = {
    undefined: lt('All'),
    true: lt('Activated'),
    false: lt('Deactivated'),
};

const defaultStatusOptions = {
    undefined: lt('All'),
    true: lt('Yes'),
    false: lt('No'),
};

interface NameCellProps {
    id: RateOverview['id'];
    name: RateOverview['name'];
}

const NameCell = ({ id, name }: NameCellProps) => {
    const { open } = useRateSidePanelContext();
    return (
        <Link standalone variant="basic" onClick={() => open(id)} data-disable-click-away>
            {name ?? <Dash />}
        </Link>
    );
};

const getEnabledStatusOptions = () => enabledstatusOptions;
const getDefaultStatusOptions = () => defaultStatusOptions;

export const columns = [
    columnHelper.accessor('name', {
        header: () => lt('RateName'),
        cell: (props) => <NameCell id={props.row.original.id} name={props.row.original.name} />,
        enableColumnFilter: true,
        enableSorting: true,
        meta: {
            filtersType: ColumnFilterType.String,
        },
    }),
    columnHelper.accessor('baseRateName', {
        header: () => lt('BaseRate'),
        enableColumnFilter: true,
        enableSorting: true,
        meta: {
            filtersType: ColumnFilterType.String,
        },
        cell: (props) =>
            props.row.original.baseRateId && props.row.original.baseRateName ? (
                <NameCell id={props.row.original.baseRateId} name={props.row.original.baseRateName} />
            ) : (
                <Dash />
            ),
    }),
    columnHelper.accessor('rateGroupName', {
        header: () => lt('RateGroupName'),
        enableColumnFilter: true,
        enableSorting: true,
        meta: {
            filtersType: ColumnFilterType.String,
        },
    }),
    columnHelper.accessor('ordering', {
        header: () => lt('Ordering'),
        enableSorting: true,
    }),
    columnHelper.accessor('rateGroupOrdering', {
        header: () => lt('RateGroupOrdering'),
        enableSorting: true,
    }),
    columnHelper.accessor('type', {
        header: () => lt('RateVisibility'),
        cell: (props) => {
            const value = props.getValue();
            return (
                <Tag variant={value === RateType.Public ? 'denim' : 'lime'}>
                    <LocalizedEnum prefix="RateType" value={value} />
                </Tag>
            );
        },
        enableColumnFilter: true,
        enableSorting: true,
        meta: {
            filtersType: ColumnFilterType.MultiSelect,
            getMultiSelectOptions: () =>
                pipe(
                    RateType,
                    values,
                    array.filter((value) => value !== RateType.AvailabilityBlock),
                    array.map((value) => ({
                        value,
                        label: <LocalizedEnum prefix="RateType" value={value} />,
                    }))
                ),
        },
    }),
    columnHelper.accessor('shortName', {
        header: () => lt('ShortName'),
        cell: (props) => props.row.original.shortName ?? <Dash />,
        enableColumnFilter: true,
        enableSorting: true,
        meta: {
            filtersType: ColumnFilterType.String,
        },
    }),
    columnHelper.accessor('externalName', {
        header: () => lt('ExternalName'),
        cell: (props) => props.row.original.externalName ?? <Dash />,
        enableColumnFilter: true,
        enableSorting: true,
        meta: {
            filtersType: ColumnFilterType.String,
        },
    }),
    columnHelper.accessor('businessSegmentName', {
        header: () => lt('BusinessSegment'),
        cell: (props) => props.row.original.businessSegmentName ?? <Dash />,
        enableColumnFilter: true,
        enableSorting: true,
        meta: {
            filtersType: ColumnFilterType.String,
        },
    }),
    columnHelper.accessor('isEnabled', {
        header: () => lt('Status'),
        cell: (props) => {
            const value = props.getValue();
            return (
                <LocalizedTextTag
                    textKey={value ? TextKey.Activated : TextKey.Deactivated}
                    variant={value ? 'success' : 'danger'}
                />
            );
        },
        enableColumnFilter: true,
        enableSorting: true,
        meta: {
            filtersType: ColumnFilterType.Bool,
            getBoolOptions: getEnabledStatusOptions,
        },
    }),
    columnHelper.accessor('isDefault', {
        header: () => lt('Default'),
        cell: (props) => {
            const value = props.getValue();
            return value ? (
                <LocalizedTextTag textKey={TextKey.Yes} variant="lime" />
            ) : (
                <LocalizedTextTag textKey={TextKey.No} variant="denim" />
            );
        },
        enableColumnFilter: true,
        enableSorting: true,
        meta: {
            filtersType: ColumnFilterType.Bool,
            getBoolOptions: getDefaultStatusOptions,
        },
    }),
    columnHelper.display({
        id: 'actions',
        meta: { align: 'end' },
        cell: ({
            row: {
                original: { id, name, isEnabled },
            },
        }) => (
            <Stack>
                <EnterpriseLink variant="basic" to="Rate/Pricing/$rateId" params={{ rateId: id }}>
                    <TooltippedIconButton
                        icon={IconName.Pricing}
                        size="small"
                        tooltip={lt('Pricing')}
                        tooltipPosition={Position.Bottom}
                        accessibleText={lt('Pricing')}
                        variant="ghost"
                        type="button"
                    />
                </EnterpriseLink>
                <RowActionCell rateId={id} rateName={name} isEnabled={isEnabled} />
            </Stack>
        ),
    }),
];
